import React, { Dispatch, FC, SetStateAction, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { typography } from 'src/config/typography';
import { colors } from 'src/config/colors';
import { media } from 'src/config/breakpoints';
import Observer from '@researchgate/react-intersection-observer';
import { useIntersectionObserver } from 'src/common/hooks/useIntersectionObserver';
import { ScrollOffset } from 'src/common/components/ScrollOffset';
import { StaticPageDTO, StaticPageSection } from 'src/common/models/staticPage';
import Blocks from 'editorjs-blocks-react-renderer';
import { StaticPageEditorContent } from 'src/public/components/static-page/StaticPageEditorContent';
import { editorjsRenderers } from 'src/config/editorjs';
import { prependCMSUrl } from 'src/config/cms';

const Attachment = styled.a`
  align-self: flex-end;
  color: ${colors.blue};
  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1rem;
  margin-top: 0.625rem;
  padding-bottom: 0.4375rem;
  text-decoration: underline;
`;

const Container = styled.div`
  ${media.w.min.px768(css`
    padding: 0 1.188rem;
  `)};
`;

const ContentContainer = styled.div`
  display: grid;
  grid-row-gap: 0.625rem;
  grid-template-areas:
    'title'
    'content';
  margin-bottom: 2.5rem;
  position: relative;

  ${media.w.min.px768(css`
    align-items: flex-end;
    grid-row-gap: 1.25rem;
    justify-content: space-between;
    margin-bottom: 3.75rem;
  `)};
`;

const ContentTitle = styled.div`
  color: ${colors.blue};
  font-size: 1.5rem;
  grid-area: title;
  line-height: 1.875rem;
`;

const Subtitle = styled.div`
  margin-bottom: 2.5rem;

  ${media.w.min.px768(css`
    margin-bottom: 3.75rem;
  `)};
`;

const Title = styled.div`
  color: ${colors.red};
  display: flex;
  flex-wrap: wrap;
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1.5rem;
  grid-area: title;
  justify-content: space-between;
  line-height: 1.875rem;
  margin-bottom: 0.625rem;
  margin-top: 0;

  ${media.w.min.px768(css`
    font-size: 2.3125rem;
    line-height: 2.875rem;
    margin-top: 2.188rem;
  `)};
`;

interface PrivacyPolicyPageContentProps {
  sections?: StaticPageSection[];
  setActiveCategory: Dispatch<SetStateAction<string | undefined>>;
  staticPage?: StaticPageDTO;
}

export const StaticPageContent: FC<PrivacyPolicyPageContentProps> = ({
  sections,
  setActiveCategory,
  staticPage,
}) => {
  const intersectionItems = useMemo(
    () => sections?.map((category) => category.id) ?? [],
    [sections],
  );

  const { handleIntersection, intersectionThreshold } = useIntersectionObserver({
    items: intersectionItems,
    onChange: setActiveCategory,
  });

  return (
    <Container>
      <Title>
        {staticPage?.data.attributes.title}

        {staticPage?.data.attributes.attachment && (
          <Attachment
            href={prependCMSUrl(staticPage.data.attributes.attachment.file.data.attributes.url)}
            target="_blank"
          >
            {staticPage.data.attributes.attachment.text}
          </Attachment>
        )}
      </Title>

      <Subtitle>
        {staticPage?.data.attributes.intro && (
          <StaticPageEditorContent>
            <Blocks data={staticPage.data.attributes.intro} renderers={editorjsRenderers} />
          </StaticPageEditorContent>
        )}
      </Subtitle>

      {sections?.map((section) => (
        <Observer
          key={section.id}
          onChange={(entry) => {
            handleIntersection(entry, section.id);
          }}
          threshold={intersectionThreshold}
        >
          <ContentContainer key={section.id}>
            <ScrollOffset id={section.id} />
            <ContentTitle>{section.title}</ContentTitle>

            <StaticPageEditorContent>
              <Blocks data={section.content} renderers={editorjsRenderers} />
            </StaticPageEditorContent>
          </ContentContainer>
        </Observer>
      ))}
    </Container>
  );
};
